import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { setProfile } from '../store/userSlice';
import {
  verifyOTP,
  resendOTP,
  persistLoginQuestionnaireFields,
} from '../services/auth';
import { fetchMyProfile } from '../services/profile';
import { getPostAuthHomeOrQuestionnaire } from '../utils/signInRedirect';
import AuthPrimaryButton from './AuthPrimaryButton';
import AuthFormContainer from './AuthFormContainer';

export default function OTPVerificationCard() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const expirationFromState = Number(location.state?.otp_expires_in);
  const initialExpirationTimer =
    Number.isFinite(expirationFromState) && expirationFromState > 0
      ? expirationFromState
      : 600;
  const [expirationTimer, setExpirationTimer] = useState(initialExpirationTimer);
  const inputRefs = useRef([]);

  // Get email from location state or localStorage
  const email = location.state?.email || localStorage.getItem('userEmail') || '';

  useEffect(() => {
    // Start countdown timer for resend (30 seconds)
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  useEffect(() => {
    // Start expiration timer (10 minutes)
    if (expirationTimer > 0) {
      const timer = setTimeout(() => setExpirationTimer(expirationTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [expirationTimer]);

  // Format timer as MM:SS
  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const handleChange = (index, value) => {
    // Only allow single digit
    if (value.length > 1) return;
    
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    // Handle paste
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then((text) => {
        if (/^\d{6}$/.test(text)) {
          const digits = text.split('');
          setOtp(digits);
          inputRefs.current[5]?.focus();
        }
      });
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setOtp(digits);
      inputRefs.current[5]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      toast.error('Please enter all 6 digits');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await verifyOTP({ email, otp: otpString });
      
      if (response && response.success === false) {
        toast.error(response.error || 'Invalid OTP. Please try again.');
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
        return;
      }
      
      if (response && response.email) {
        let merged = { ...response };
        try {
          const profile = await fetchMyProfile();
          if (typeof profile.medical_questionnaire_completed === 'boolean') {
            merged.medical_questionnaire_completed = profile.medical_questionnaire_completed;
          }
          if (
            profile.number_of_questions_filled !== null &&
            profile.number_of_questions_filled !== undefined &&
            Number.isFinite(Number(profile.number_of_questions_filled))
          ) {
            merged.number_of_questions_filled = Number(profile.number_of_questions_filled);
          }
          if (profile.questionnaire_completion_percentage != null) {
            merged.questionnaire_completion_percentage = profile.questionnaire_completion_percentage;
          }
          if (profile.sections_completed != null) {
            merged.sections_completed = profile.sections_completed;
          }
          if (profile.section_progress != null) {
            merged.section_progress = profile.section_progress;
          }
          if (profile.totalSections != null) {
            merged.totalSections = profile.totalSections;
          }
          persistLoginQuestionnaireFields({
            medical_questionnaire_completed: merged.medical_questionnaire_completed,
            number_of_questions_filled: merged.number_of_questions_filled,
            questionnaire_completion_percentage: merged.questionnaire_completion_percentage,
            sections_completed: merged.sections_completed,
            section_progress: merged.section_progress,
            total_sections: merged.totalSections,
          });
        } catch (_) {
          // profile/me may fail; OTP payload still applies
        }

        dispatch(
          setProfile({
            fullName: merged.fullName,
            email: merged.email,
            gender: merged.gender,
            dateOfBirth: merged.dateOfBirth,
            role: merged.role,
            organization: merged.organization,
            on_boarding_completed: merged.on_boarding_completed,
            on_boarding_step: merged.on_boarding_step,
            must_change_password: Boolean(merged.must_change_password),
            ...(typeof merged.medical_questionnaire_completed === 'boolean'
              ? { medical_questionnaire_completed: merged.medical_questionnaire_completed }
              : {}),
            ...(merged.number_of_questions_filled !== null &&
            merged.number_of_questions_filled !== undefined &&
            Number.isFinite(Number(merged.number_of_questions_filled))
              ? { number_of_questions_filled: Number(merged.number_of_questions_filled) }
              : {}),
            ...(merged.questionnaire_completion_percentage != null
              ? { questionnaire_completion_percentage: merged.questionnaire_completion_percentage }
              : {}),
            ...(merged.sections_completed != null &&
            Number.isFinite(Number(merged.sections_completed))
              ? { sections_completed: Number(merged.sections_completed) }
              : {}),
            ...(merged.section_progress != null && Number.isFinite(Number(merged.section_progress))
              ? { section_progress: Number(merged.section_progress) }
              : {}),
            ...(merged.totalSections != null && Number.isFinite(Number(merged.totalSections))
              ? { totalSections: Number(merged.totalSections) }
              : {}),
          })
        );

        toast.success('OTP verified successfully!');

        const normalizedRole = String(merged.role || '').trim();
        const normalizedStep = String(merged.on_boarding_step || '').trim();
        const needsPersonalDetails =
          merged.on_boarding_completed === false &&
          normalizedStep === 'Personal Details';
        const isDoctorFlow =
          normalizedRole === 'Doctor/Nurse' ||
          normalizedRole.toLowerCase() === 'doctor/nurse';

        if (isDoctorFlow) {
          navigate('/prescription');
          return;
        }

        if (needsPersonalDetails) {
          navigate('/personal-information');
          return;
        }

        let viewAs = null;
        try {
          viewAs = localStorage.getItem('supervisorViewAsRole');
        } catch (_) {
          // ignore
        }

        navigate('/activate-barcode', {
          state: {
            allowSkip: true,
            nextPath: getPostAuthHomeOrQuestionnaire(
              merged.medical_questionnaire_completed,
              normalizedRole,
              viewAs
            ),
          },
        });
      } else {
        toast.error('Invalid OTP. Please try again.');
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      // Handle unexpected errors
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to verify OTP. Please try again.';
      toast.error(errorMessage);
      // Clear OTP on error
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;

    setIsResending(true);
    
    try {
      const response = await resendOTP({ email });
      
      if (response.success) {
        toast.success(response?.data?.message || 'OTP has been resent to your email');
        setCountdown(30);
        setExpirationTimer(600);
      } else {
        toast.error(response.error || 'Failed to resend OTP. Please try again.');
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to resend OTP. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <AuthFormContainer>
      <div className="flex flex-col items-center gap-1 md:gap-2 w-full">
        <p className="text-[#2B3133] font-libreBaskerville text-2xl md:text-3xl leading-[1em] text-center">
          Verify Your Account
        </p>
        <p className="text-[#889696] font-poppins text-sm md:text-base font-normal text-center">
          A verification code has been sent to{' '}
          <span className="font-medium text-[#2B3133]">{email || 'your email'}</span>
          . Please check your inbox and enter the code below. The code expires in{' '}
          <span className="font-medium text-[#2B3133]">{formatTimer(expirationTimer)}</span>.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-6">
        {/* OTP Input Boxes - XXX-XXX grouping */}
        <div className="flex items-center justify-center gap-1.5 md:gap-2 w-full">
          {otp.map((digit, index) => (
            <div key={index} className="relative">
              {index === 3 && (
                <span className="absolute -left-2 md:-left-2.5 top-1/2 -translate-y-1/2 text-[#CBD5E0] font-poppins font-semibold text-lg md:text-xl select-none">
                  –
                </span>
              )}
              <input
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                className={`w-10 h-12 md:w-12 md:h-14 rounded-xl border-2 text-center text-xl md:text-2xl font-poppins font-semibold focus:outline-none transition-all duration-200 ${
                  digit
                    ? 'border-[#119975] bg-[#E7F6EF] text-[#119975]'
                    : 'border-[#CBD5E0] bg-[#F8F8F8] text-[#2B3133]'
                } focus:border-[#119975] focus:bg-white focus:ring-2 focus:ring-[#119975]/20`}
                style={{
                  animation: digit ? 'digitEnter 0.3s ease-out' : 'none',
                }}
              />
            </div>
          ))}
        </div>

        <AuthPrimaryButton
          type="submit"
          disabled={isSubmitting || otp.join('').length !== 6}
          className="mt-4"
        >
          {isSubmitting ? 'Verifying...' : 'Verify'}
        </AuthPrimaryButton>
      </form>

      {/* Didn't receive a code? */}
      <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 flex-wrap">
        <p className="text-[#2B3133] font-poppins text-sm text-center sm:text-left">
          Didn&apos;t receive a code?
        </p>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleResend}
            disabled={countdown > 0 || isResending}
            className="text-[#119975] font-poppins text-sm font-medium underline hover:text-[#0d7a5f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {countdown > 0
              ? `Resend in ${countdown}s`
              : isResending
              ? 'Sending...'
              : 'Resend Code'}
          </button>
          <span className="text-[#CBD5E0]">|</span>
          <button
            type="button"
            onClick={() => navigate('/signup')}
            className="text-[#119975] font-poppins text-sm font-medium underline hover:text-[#0d7a5f] transition-colors"
          >
            Change Email
          </button>
        </div>
      </div>

      <style>{`
        @keyframes digitEnter {
          0% { transform: scale(0.9); opacity: 0.5; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </AuthFormContainer>
  );
}

